import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";

const AdminCustomerProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Fetch user data
        const userDocRef = doc(db, "users", userId);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          setUserData(userDocSnap.data());
        } else {
          console.warn("No such user!");
          setUserData(null);
        }

        // Fetch user's subscriptions
        const subscriptionsQuery = query(
          collection(db, `orders/${userId}/subscriptions`)
        );
        const subscriptionsSnap = await getDocs(subscriptionsQuery);
        
        const subscriptionsData = subscriptionsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setSubscriptions(subscriptionsData);
        
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading customer profile...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-xl shadow-md border border-green-200 text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">Customer profile not found.</p>
          <button 
            onClick={() => navigate(-1)} 
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const activeSubscription = subscriptions.find(sub => sub.status === 'active');
  const allSubscriptions = subscriptions.length;

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Admin Panel</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-4xl mx-auto">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-green-600 hover:text-green-800 mr-2"
          >
            ← Dashboard
          </button>
          <span className="text-gray-400 mx-2">/</span>
          <button
            onClick={() => navigate('/admin/customers')}
            className="text-green-600 hover:text-green-800 mr-2"
          >
            Customers
          </button>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-600">Profile</span>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-green-200 overflow-hidden">
          {/* Customer Header */}
          <div className="bg-green-50 px-6 py-4 border-b border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-green-700">👤 Customer Profile</h2>
                <p className="text-gray-600">User ID: {userId}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Total Subscriptions</div>
                <div className="text-2xl font-bold text-green-700">{allSubscriptions}</div>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-green-700 mb-4">📋 Personal Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Name:</span>
                    <span className="text-gray-900">{userData.name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Email:</span>
                    <a 
                      href={`mailto:${userData.email}`} 
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {userData.email || 'N/A'}
                    </a>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Phone:</span>
                    {userData.phone ? (
                      <a 
                        href={`tel:${userData.phone}`} 
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {userData.phone}
                      </a>
                    ) : (
                      <span className="text-gray-900">N/A</span>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Address:</span>
                    <span className="text-gray-900 text-right max-w-xs">{userData.address || 'N/A'}</span>
                  </div>
                  {userData.lat && userData.lng && (
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Location:</span>
                      <span className="text-gray-900 text-sm">
                        {userData.lat}, {userData.lng}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Subscription */}
              <div>
                <h3 className="text-lg font-semibold text-green-700 mb-4">📦 Current Subscription</h3>
                {activeSubscription ? (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Plan:</span>
                        <span className="text-green-700 font-semibold">{activeSubscription.planName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Status:</span>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                          {activeSubscription.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Rotis/Day:</span>
                        <span className="text-gray-900">{activeSubscription.rotisPerDay}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Delivery Slot:</span>
                        <span className="text-gray-900">{activeSubscription.deliverySlot || 'N/A'}</span>
                      </div>
                      {activeSubscription.startDate && (
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Start Date:</span>
                          <span className="text-gray-900">
                            {new Date(activeSubscription.startDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-gray-500 italic">No active subscription</p>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription History */}
            {subscriptions.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-green-700 mb-4">📊 Subscription History</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Plan</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Rotis/Day</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Start Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((subscription, index) => (
                        <tr key={subscription.id} className="border-b">
                          <td className="px-4 py-2">{subscription.planName}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              subscription.status === "active"
                                ? "bg-green-100 text-green-700"
                                : subscription.status === "paused"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700"
                            }`}>
                              {subscription.status}
                            </span>
                          </td>
                          <td className="px-4 py-2">{subscription.rotisPerDay}</td>
                          <td className="px-4 py-2">
                            {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => navigate("/admin/customers")}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all"
              >
                ← Back to Customers
              </button>
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
              >
                ← Back to Dashboard
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all">
                ✏️ Edit Customer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomerProfile; 